import React, { createContext, useContext, useReducer, useRef } from "react";
import {
  COLLECTION_ID,
  Space,
  Track,
} from "@highnote/server/src/core/entities";
import { QueryConstraint } from "firebase/firestore";
import {
  WatcherComponentId,
  WatcherState,
  useWatcherManagementV2,
} from "../watchers/useWatcherV2";
import { useGlobalSpaceTracks } from "../spaceTracks/useGlobalSpaceTracks";

type GlobalTracksAction =
  | { type: "SET_TRACK"; payload: Track }
  | { type: "ADD_TRACKS"; payload: Track[] }
  | {
      type: "ADD_CHILD_TRACKS";
      payload: {
        parentSpaceId: string;
        entities: Track[];
        // TODO: Use trackOrder in the future to set orders.
        // Currently itemsOrder is an array of both attachments + tracks, but we only want tracks.
        // Need an easier/efficient way to determine and isolate the tracks in order from it.
        trackOrder?: string[];
      };
    };

type GlobalTracksState = {
  tracks: Map<string, Track>;
  // Map of parent space id to array of child track ids, sourced from the
  // legacy `tracks-v2 where spaceId == S` query. This ONLY ever reflects a
  // track's ORIGIN space — a track borrowed into another space never shows
  // up here for that borrowing space. See `childTracks` (derived, below)
  // on `useGlobalTracks()` for the full membership union.
  legacyChildTracks: Map<Space["id"], Array<Track["id"]>>;
};

function tracksReducer(
  state: GlobalTracksState,
  action: GlobalTracksAction,
): GlobalTracksState {
  switch (action.type) {
    case "SET_TRACK": {
      const newTracks = new Map(state.tracks);
      newTracks.set(action.payload.id, action.payload);
      return { ...state, tracks: newTracks };
    }
    case "ADD_TRACKS": {
      const newTracks = new Map(state.tracks);
      action.payload.forEach((track) => {
        newTracks.set(track.id, track);
      });
      return { ...state, tracks: newTracks };
    }
    case "ADD_CHILD_TRACKS": {
      const { parentSpaceId, entities } = action.payload;
      const newLegacyChildTracksState = new Map(state.legacyChildTracks);
      const newTrackState = new Map(state.tracks);

      // `action.payload` returns a new array of entities, so we reset the child track id's
      // here to ensure we're not adding duplicates, or keeping stale data.
      const newChildTrackIds = new Set<string>();

      entities.forEach((track) => {
        // Add new children to the Set to ensure all entries are unique
        newChildTrackIds.add(track.id);
        // Add or update child tracks in the global tracks map
        newTrackState.set(track.id, track);
      });

      // Update child spaces map with the unique list of child IDs
      newLegacyChildTracksState.set(
        parentSpaceId,
        Array.from(newChildTrackIds),
      );

      return {
        ...state,
        tracks: newTrackState,
        legacyChildTracks: newLegacyChildTracksState,
      };
    }
    default:
      return state;
  }
}

// Backs the `childTracks` union derived in `useGlobalTracks()` below. Held
// as a ref in ONE `GlobalTracksContextProvider` instance (not per-caller
// `useMemo` state) so every `useGlobalTracks()` call anywhere in the tree
// reads/writes the SAME cache: the union is computed once and reused, not
// once per consuming component. See `useGlobalTracks()` for how it's used.
type ChildTracksCache = {
  lastRowChildTracks: Map<Space["id"], Array<Track["id"]>> | null;
  lastLegacyChildTracks: Map<Space["id"], Array<Track["id"]>> | null;
  unionedChildTracks: Map<Space["id"], Array<Track["id"]>>;
};

type GlobalTracksContext = {
  globalTracks: GlobalTracksState;
  globalTracksWatchers: WatcherState;
  globalTracksDispatch: React.Dispatch<GlobalTracksAction>;
  childTracksCacheRef: React.MutableRefObject<ChildTracksCache>;
  manageGlobalTrackWatchers: ({
    action,
    entityId,
    componentId,
    addEntity,
    removeEntity,
  }: {
    action: "attach" | "detach";
    entityId: string;
    componentId: WatcherComponentId;
    addEntity: (entity: Track) => void;
    removeEntity: (entityId: string) => void;
  }) => Promise<void>;
  manageGlobalTracksWatchers: ({
    action,
    watcherKey,
    componentId,
    constraints,
    setEntities,
    limit,
  }: {
    action: "attach" | "detach";
    watcherKey: string;
    componentId: WatcherComponentId;
    constraints?: QueryConstraint[];
    limit?: number;
    setEntities: (entities: Track[]) => void;
  }) => Promise<void>;
};

const GlobalTracksContext = createContext<GlobalTracksContext>(
  null as GlobalTracksContext,
);

export const GlobalTracksContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [globalTracks, globalTracksDispatch] = useReducer(tracksReducer, {
    tracks: new Map(),
    legacyChildTracks: new Map(),
  });

  const childTracksCacheRef = useRef<ChildTracksCache>({
    lastRowChildTracks: null,
    lastLegacyChildTracks: null,
    unionedChildTracks: new Map(),
  });

  const { activeWatchers: globalTracksWatchers, manageWatcher } =
    useWatcherManagementV2<Track>();

  const manageGlobalTrackWatchers = async ({
    action,
    entityId,
    componentId,
    addEntity,
    removeEntity,
  }: {
    action: "attach" | "detach";
    entityId: string;
    componentId: WatcherComponentId;
    addEntity: (entity: Track) => void;
    removeEntity: (entityId: string) => void;
  }) => {
    await manageWatcher({
      watcherType: "single",
      action,
      componentId,
      entityId,
      collectionId: COLLECTION_ID.TRACK,
      ...(action === "attach" && {
        onEntityChange: {
          add: addEntity,
          remove: removeEntity,
        },
      }),
    });
  };

  const manageGlobalTracksWatchers = async ({
    action,
    watcherKey,
    componentId,
    constraints,
    setEntities,
    limit,
  }: {
    action: "attach" | "detach";
    watcherKey: string;
    componentId: WatcherComponentId;
    constraints?: QueryConstraint[];
    limit?: number;
    setEntities: (entities: Track[]) => void;
  }) => {
    await manageWatcher({
      watcherType: "multiple",
      action,
      componentId,
      watcherKey,
      collectionId: COLLECTION_ID.TRACK,
      ...(action === "attach" && {
        constraints: constraints || [],
        onEntitiesChange: setEntities,
        limit,
      }),
    });
  };

  return (
    <GlobalTracksContext.Provider
      value={{
        globalTracks,
        globalTracksWatchers,
        globalTracksDispatch,
        childTracksCacheRef,
        manageGlobalTrackWatchers,
        manageGlobalTracksWatchers,
      }}
    >
      {children}
    </GlobalTracksContext.Provider>
  );
};

export const useGlobalTracks = () => {
  const context = useContext(GlobalTracksContext);

  if (!context) {
    throw new Error(
      "useGlobalTracks must be used within a GlobalTracksContextProvider",
    );
  }

  const { globalSpaceTracks } = useGlobalSpaceTracks();
  const { legacyChildTracks } = context.globalTracks;
  const { rowChildTracks } = globalSpaceTracks;

  // `childTracks` is the DERIVED union of membership from the two sources:
  // - `legacyChildTracks`: the legacy `tracks-v2 where spaceId == S` query.
  //   Only ever reflects a track's ORIGIN space.
  // - `rowChildTracks`: `space-tracks-v1` join rows (`useGlobalSpaceTracks`).
  //   Reflects a space's full membership, origin AND borrowed.
  //
  // ADD_CHILD_TRACKS/SET_SPACE_TRACK_ROWS (above, and in
  // useGlobalSpaceTracks.tsx) REPLACE the whole per-space legacy/row list on
  // every snapshot — two sources writing that one slice would erase each
  // other. So the slices stay separate and this union is computed here
  // instead. Row ids go first so a track that's been backfilled into
  // `space-tracks-v1` (i.e. now has a row for its own origin space too)
  // resolves row-first, not legacy-first.
  //
  // Both `rowChildTracks` and `legacyChildTracks` get a NEW Map identity on
  // EVERY dispatch, for ANY single space — the reducers only replace that
  // one space's entry, but wrap it in a fresh Map. A plain `useMemo` keyed
  // on the two Maps would therefore rebuild every OTHER space's array too,
  // on every dispatch, anywhere — and `useGlobalTracks()` is called from
  // many components, each with its own independent `useMemo`, multiplying
  // that cost. Consumers that read `childTracks.get(spaceId)` in their own
  // `useMemo`/`useEffect` deps (SpaceHomeV2, ExpandableSpaceRow, SpaceTrack,
  // ...) would then invalidate on unrelated spaces' updates.
  //
  // Instead, `childTracksCacheRef` (one instance per
  // `GlobalTracksContextProvider`, NOT per-caller) is used to compute the
  // union once and share it: a space's array is only rebuilt (new
  // reference) when its own unioned id list actually changed content —
  // every other space's array reference, and the Map itself when nothing
  // changed at all, are reused as-is.
  const cache = context.childTracksCacheRef.current;
  let childTracks = cache.unionedChildTracks;

  if (
    cache.lastRowChildTracks !== rowChildTracks ||
    cache.lastLegacyChildTracks !== legacyChildTracks
  ) {
    const spaceIds = new Set<string>([
      ...Array.from(rowChildTracks.keys()),
      ...Array.from(legacyChildTracks.keys()),
    ]);

    const nextChildTracks = new Map<string, string[]>();
    let anySpaceChanged = spaceIds.size !== cache.unionedChildTracks.size;

    spaceIds.forEach((spaceId) => {
      const rowIds = rowChildTracks.get(spaceId) || [];
      const legacyIds = legacyChildTracks.get(spaceId) || [];
      const mergedIds = Array.from(new Set([...rowIds, ...legacyIds]));

      const previousIds = cache.unionedChildTracks.get(spaceId);
      const isUnchanged =
        !!previousIds &&
        previousIds.length === mergedIds.length &&
        previousIds.every((id, index) => id === mergedIds[index]);

      const idsToUse = isUnchanged ? previousIds : mergedIds;
      if (idsToUse !== previousIds) anySpaceChanged = true;
      nextChildTracks.set(spaceId, idsToUse);
    });

    childTracks = anySpaceChanged ? nextChildTracks : cache.unionedChildTracks;

    cache.lastRowChildTracks = rowChildTracks;
    cache.lastLegacyChildTracks = legacyChildTracks;
    cache.unionedChildTracks = childTracks;
  }

  return {
    ...context,
    globalTracks: {
      ...context.globalTracks,
      childTracks,
    },
  };
};
